import { useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api } from "../queries";
import Instant from "../components/Instant";
import ProductChips from "../components/ProductChips";
import ProductFilter from "../components/ProductFilter";
import TagChips from "../components/TagChips";
import TagFilter from "../components/TagFilter";
import { CoverageRing, LivenessDot, ProductIcon, VerdictShield } from "../glyphs";
import { useVirtualList } from "../hooks/useVirtualList";
import { applyTagFilter, NO_TAG_FILTER, type TagFilterValue } from "../registry";
import { applyCreatedRange } from "../time";
import type { Run, RunSignals } from "../types";
import {
  EMPORIA_ENVIRONMENT_VALUES,
  INTERFACE_DEVICE_VALUES,
  RUN_KIND_VALUES,
  RUN_STATUS_VALUES,
} from "../vocabulary.generated";

const PAGE_SIZE = 25;
const ROW_HEIGHT = 56;
const HEADER_HEIGHT = 30;
// Shared testing vocabularies (qa-vocabulary): a new value there is a dependency bump.
const STATUSES = RUN_STATUS_VALUES;
const SCOPES = RUN_KIND_VALUES;
const RUN_KINDS = [
  "single",
  "targeted",
  "feature",
  "change_set",
  "smoke",
  "release_regression",
  "full_regression",
  "continuous",
  "beta",
];
const PRIORITIES = ["low", "normal", "high", "urgent"];
const ENVIRONMENTS = EMPORIA_ENVIRONMENT_VALUES;
const DEVICES = INTERFACE_DEVICE_VALUES;

/**
 * Per-run signal chips. The stored reduction summary is used when there is one;
 * the live signals endpoint is preferred, because an unreduced run still has
 * gaps and the list should not pretend otherwise.
 */
function signals(run: Run, live?: RunSignals) {
  if (live) {
    return {
      gaps: live.gaps_count,
      regressions: live.unresolved_regressions,
      blocked: live.blocked_count,
      decision: live.decision,
      coveragePct: live.coverage_pct,
    };
  }
  const summary = run.summary as
    | {
        gaps?: unknown[];
        regressions?: { unresolved?: number };
        status_counts?: Record<string, number>;
      }
    | null;
  if (!summary) return null;
  return {
    gaps: Array.isArray(summary.gaps) ? summary.gaps.length : 0,
    regressions: summary.regressions?.unresolved ?? 0,
    blocked: summary.status_counts?.blocked ?? 0,
    decision: run.go_no_go,
    coveragePct: run.coverage_pct,
  };
}

const DAY_MS = 24 * 60 * 60 * 1000;
function bucket(iso: string): string {
  const age = Date.now() - new Date(iso).getTime();
  if (age < DAY_MS) return "Today";
  if (age < 7 * DAY_MS) return "This week";
  return "Older";
}

function ownerInitials(actor: string): string {
  const local = actor.split("@")[0] ?? actor;
  const parts = local.split(/[._-]+/).filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("") || actor.slice(0, 2);
}

function FacetChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`badge ${active ? "badge-primary" : "badge-outline"}`}
    >
      {children}
    </button>
  );
}

function RunRow({
  run,
  live,
  compareMode,
  selected,
  onToggle,
}: {
  run: Run;
  live?: RunSignals;
  compareMode: boolean;
  selected: boolean;
  onToggle: () => void;
}) {
  const sig = signals(run, live);
  return (
    <div
      className="density-row flex items-center gap-3 px-3 hover:bg-base-200 rounded-lg overflow-hidden"
      style={{ height: ROW_HEIGHT }}
    >
      {compareMode && (
        <input
          type="checkbox"
          className="checkbox checkbox-sm shrink-0"
          checked={selected}
          onChange={onToggle}
          aria-label={`Select run #${run.run_number} for comparison`}
        />
      )}
      <VerdictShield verdict={sig?.decision ?? run.go_no_go} size="sm" />
      <Link
        className="link link-primary font-medium whitespace-nowrap shrink-0"
        to={`/runs/${run.id}`}
      >
        #{run.run_number}
      </Link>
      <CoverageRing pct={sig?.coveragePct ?? run.coverage_pct} size={32} />
      {sig && (
        <span className="hidden sm:flex items-center gap-1 shrink-0">
          {sig.gaps > 0 && <span className="badge badge-warning badge-sm">{sig.gaps} gaps</span>}
          {sig.regressions > 0 && (
            <span className="badge badge-error badge-sm">{sig.regressions} regr</span>
          )}
          {sig.blocked > 0 && <span className="badge badge-neutral badge-sm">{sig.blocked} blk</span>}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block text-sm truncate">
          {run.scope_kind.replaceAll("_", " ")} · {run.scope_ref}
        </span>
        <span className="flex items-center gap-2 overflow-hidden">
          {run.run_kind && (
            <span className="badge badge-outline badge-xs shrink-0">
              {run.run_kind.replaceAll("_", " ")}
            </span>
          )}
          {run.priority && (
            <span className="badge badge-ghost badge-xs shrink-0">{run.priority}</span>
          )}
          <ProductChips keys={run.product_area} empty="" />
          <TagChips tags={run.tags} />
        </span>
      </span>
      <span className="hidden md:flex items-center gap-1 shrink-0" title="Product area">
        {run.product_area.slice(0, 4).map((area) => (
          <ProductIcon key={area} area={area} />
        ))}
      </span>
      {run.owners.length > 0 && (
        <span
          className="hidden sm:flex items-center gap-0.5 shrink-0"
          title={run.owners.map((o) => `${o.actor} (${o.role})`).join(", ")}
        >
          {run.owners.slice(0, 3).map((o) => (
            <span key={o.id} className="badge badge-ghost badge-sm">
              {ownerInitials(o.actor)}
            </span>
          ))}
        </span>
      )}
      <LivenessDot liveness={run.liveness} />
      <span className="text-xs opacity-60 whitespace-nowrap w-28 text-right">
        <Instant iso={run.created_at} />
      </span>
    </div>
  );
}

type DisplayItem = { kind: "header"; label: string } | { kind: "row"; run: Run };

export default function RunsList() {
  const navigate = useNavigate();
  const [statuses, setStatuses] = useState<string[]>([]);
  const [scopeKind, setScopeKind] = useState("");
  const [runKind, setRunKind] = useState("");
  const [priority, setPriority] = useState("");
  const [environment, setEnvironment] = useState("");
  const [device, setDevice] = useState("");
  const [parameter, setParameter] = useState("");
  const [parameterValue, setParameterValue] = useState("");
  const [productArea, setProductArea] = useState("");
  const [createdAfter, setCreatedAfter] = useState("");
  const [createdBefore, setCreatedBefore] = useState("");
  const [tagFilter, setTagFilter] = useState<TagFilterValue>(NO_TAG_FILTER);
  const [staleOnly, setStaleOnly] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  // Filters only; the cursor carries the position, so changing a filter resets
  // paging by changing the query key.
  const filterParams = useMemo(() => {
    const p = new URLSearchParams();
    for (const status of statuses) p.append("status", status);
    if (scopeKind) p.set("scope_kind", scopeKind);
    if (runKind) p.set("run_kind", runKind);
    if (priority) p.set("priority", priority);
    if (environment) p.set("emporia_environment", environment);
    if (device) p.set("interface_device", device);
    if (parameter) p.set("parameter", parameter);
    if (parameter && parameterValue) p.set("parameter_value", parameterValue);
    if (productArea) p.set("product_area", productArea);
    if (staleOnly) p.set("stale", "true");
    applyCreatedRange(p, createdAfter, createdBefore);
    applyTagFilter(p, tagFilter);
    return p;
  }, [
    statuses,
    scopeKind,
    runKind,
    priority,
    environment,
    device,
    parameter,
    parameterValue,
    productArea,
    createdAfter,
    createdBefore,
    tagFilter,
    staleOnly,
  ]);

  const query = useInfiniteQuery({
    queryKey: ["runs", "page", filterParams.toString()],
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams(filterParams);
      p.set("limit", String(PAGE_SIZE));
      if (pageParam) p.set("cursor", pageParam);
      return api.listRuns(p);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  const total = query.data?.pages[0]?.total ?? 0;
  const items = useMemo(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data],
  );

  const ids = useMemo(() => items.map((r) => r.id), [items]);
  const signalsQuery = useQuery({
    queryKey: ["run-signals", ids.join(",")],
    queryFn: () => api.getRunSignals(ids),
    enabled: ids.length > 0,
  });
  const facetsQuery = useQuery({
    queryKey: ["run-facets"],
    queryFn: () => api.getRunFacets(),
  });
  const facets = facetsQuery.data;
  const liveSignals = useMemo(
    () => new Map((signalsQuery.data ?? []).map((s) => [s.run_id, s])),
    [signalsQuery.data],
  );

  // Pages arrive newest first, so the time buckets stay contiguous as they append.
  const display = useMemo<DisplayItem[]>(() => {
    const order = ["Today", "This week", "Older"];
    const map = new Map<string, Run[]>();
    for (const run of items) {
      const key = bucket(run.created_at);
      const list = map.get(key) ?? [];
      list.push(run);
      map.set(key, list);
    }
    const out: DisplayItem[] = [];
    for (const key of order) {
      const runs = map.get(key);
      if (!runs) continue;
      out.push({ kind: "header", label: `${key} · ${runs.length}` });
      for (const run of runs) out.push({ kind: "row", run });
    }
    return out;
  }, [items]);

  const heights = useMemo(
    () => display.map((d) => (d.kind === "header" ? HEADER_HEIGHT : ROW_HEIGHT)),
    [display],
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const virtual = useVirtualList(heights, containerRef);

  const toggleStatus = (status: string) =>
    setStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status],
    );
  const toggleSelected = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  const hasFilters =
    statuses.length > 0 ||
    Boolean(
      scopeKind ||
        runKind ||
        priority ||
        environment ||
        device ||
        parameter ||
        parameterValue ||
        productArea ||
        createdAfter ||
        createdBefore ||
        staleOnly,
    ) ||
    Boolean(tagFilter.key);
  const clearFilters = () => {
    setStatuses([]);
    setScopeKind("");
    setRunKind("");
    setPriority("");
    setEnvironment("");
    setDevice("");
    setParameter("");
    setParameterValue("");
    setProductArea("");
    setCreatedAfter("");
    setCreatedBefore("");
    setTagFilter(NO_TAG_FILTER);
    setStaleOnly(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <h1 className="text-2xl font-bold">Runs</h1>
        <div className="flex items-center gap-2">
          {compareMode && selected.length === 2 && (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => navigate(`/runs/${selected[0]}/compare/${selected[1]}`)}
            >
              Compare selected
            </button>
          )}
          <button
            type="button"
            className={`btn btn-sm ${compareMode ? "btn-active" : ""}`}
            onClick={() => {
              setCompareMode((v) => !v);
              setSelected([]);
            }}
          >
            Compare
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {STATUSES.map((status) => (
          <FacetChip
            key={status}
            active={statuses.includes(status)}
            onClick={() => toggleStatus(status)}
          >
            {status}
            {facets && facets.status[status] ? (
              <span className="opacity-60">{facets.status[status]}</span>
            ) : null}
          </FacetChip>
        ))}
        <span className="w-px h-5 bg-base-300 mx-1" />
        {SCOPES.map((scope) => (
          <FacetChip
            key={scope}
            active={scopeKind === scope}
            onClick={() => setScopeKind(scopeKind === scope ? "" : scope)}
          >
            {scope.replaceAll("_", " ")}
            {facets && facets.scope_kind[scope] ? (
              <span className="opacity-60">{facets.scope_kind[scope]}</span>
            ) : null}
          </FacetChip>
        ))}
        <span className="w-px h-5 bg-base-300 mx-1" />
        <FacetChip active={staleOnly} onClick={() => setStaleOnly((v) => !v)}>
          stale only
        </FacetChip>
      </div>

      <div className="flex flex-wrap gap-2 items-end">
        <label className="form-control" htmlFor="runs-kind">
          <span className="label-text text-xs">Kind</span>
          <select
            id="runs-kind"
            className="select select-bordered select-sm"
            value={runKind}
            onChange={(e) => setRunKind(e.target.value)}
          >
            <option value="">any</option>
            {RUN_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {kind.replaceAll("_", " ")}
              </option>
            ))}
          </select>
        </label>
        <label className="form-control" htmlFor="runs-priority">
          <span className="label-text text-xs">Priority</span>
          <select
            id="runs-priority"
            className="select select-bordered select-sm"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          >
            <option value="">any</option>
            {PRIORITIES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="form-control" htmlFor="runs-env">
          <span className="label-text text-xs">Environment</span>
          <select
            id="runs-env"
            className="select select-bordered select-sm"
            value={environment}
            onChange={(e) => setEnvironment(e.target.value)}
          >
            <option value="">any</option>
            {ENVIRONMENTS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="form-control" htmlFor="runs-device">
          <span className="label-text text-xs">Device</span>
          <select
            id="runs-device"
            className="select select-bordered select-sm"
            value={device}
            onChange={(e) => setDevice(e.target.value)}
          >
            <option value="">any</option>
            {DEVICES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="form-control" htmlFor="runs-parameter">
          <span className="label-text text-xs">Parameter</span>
          <input
            id="runs-parameter"
            type="text"
            placeholder="account_type"
            className="input input-bordered input-sm w-32"
            value={parameter}
            onChange={(e) => setParameter(e.target.value.trim())}
          />
        </label>
        <label className="form-control" htmlFor="runs-parameter-value">
          <span className="label-text text-xs">Value</span>
          <input
            id="runs-parameter-value"
            type="text"
            placeholder="premium"
            className="input input-bordered input-sm w-32"
            value={parameterValue}
            disabled={!parameter}
            onChange={(e) => setParameterValue(e.target.value)}
          />
        </label>
        <TagFilter idPrefix="runs" value={tagFilter} onChange={setTagFilter} />
        <ProductFilter idPrefix="runs" value={productArea} onChange={setProductArea} />
        <label className="form-control" htmlFor="runs-from">
          <span className="label-text text-xs">From</span>
          <input
            id="runs-from"
            type="date"
            className="input input-bordered input-sm"
            value={createdAfter}
            onChange={(e) => setCreatedAfter(e.target.value)}
          />
        </label>
        <label className="form-control" htmlFor="runs-to">
          <span className="label-text text-xs">To</span>
          <input
            id="runs-to"
            type="date"
            className="input input-bordered input-sm"
            value={createdBefore}
            onChange={(e) => setCreatedBefore(e.target.value)}
          />
        </label>
      </div>

      {query.isLoading && <span className="loading loading-spinner" />}
      {query.isError && (
        <div role="alert" className="alert alert-error">
          {query.error instanceof Error ? query.error.message : "Failed to load runs"}
        </div>
      )}

      {display.length > 0 && (
        <div
          ref={containerRef}
          onScroll={virtual.onScroll}
          className="h-[65vh] overflow-auto"
          role="list"
          aria-rowcount={display.length}
        >
          <div style={{ height: virtual.topPad }} />
          {display.slice(virtual.start, virtual.end).map((item) =>
            item.kind === "header" ? (
              <h2
                key={`h-${item.label}`}
                className="text-xs uppercase tracking-wide opacity-50 px-3 flex items-center"
                style={{ height: HEADER_HEIGHT }}
              >
                {item.label}
              </h2>
            ) : (
              <RunRow
                key={item.run.id}
                run={item.run}
                live={liveSignals.get(item.run.id)}
                compareMode={compareMode}
                selected={selected.includes(item.run.id)}
                onToggle={() => toggleSelected(item.run.id)}
              />
            ),
          )}
          <div style={{ height: virtual.bottomPad }} />
        </div>
      )}

      {!query.isLoading && items.length === 0 && (
        <div className="text-center py-10 space-y-2">
          <p className="opacity-70">No runs match these filters.</p>
          <p className="text-sm opacity-50">
            Runs are planned by Mission Control — this list is the ledger of what was run.
          </p>
          {hasFilters && (
            <button type="button" className="btn btn-sm" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
      )}

      <div className="flex justify-between items-center">
        <span className="text-sm opacity-70">
          {items.length} of {total} run{total === 1 ? "" : "s"}
        </span>
        {query.hasNextPage && (
          <button
            type="button"
            className="btn btn-sm"
            disabled={query.isFetchingNextPage}
            onClick={() => query.fetchNextPage()}
          >
            {query.isFetchingNextPage ? "Loading…" : "Load more"}
          </button>
        )}
      </div>
    </div>
  );
}
